/**
 * SPEC-TrainingSimulator §7.2 — Answer key editor at /train/manage/item/:id.
 *
 * Left: the trainee's view of the delivery (SimulatorDeliveryView — the same
 * clone the runner mounts in Phase B) with a collapsible "Model says" box.
 * Right: Q1–Q11 keyed with the trainee's own controls (AnswerKeyForm) plus a
 * rationale per question. Readiness badges update live from the shared
 * `readinessByPhase` helper — the same function the server's serve gate
 * runs, so badge and gate can never disagree (§4.1).
 *
 * Save bumps `answer_key_version` (D22). When answers exist against an older
 * key the page shows the count and a Regrade button; nothing is regraded
 * silently.
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { useRole } from "@/hooks/use-role";
import { Starling } from "@/lib/starling-brand";
import { useRegradeSimulatorItem, useRefreshVerifiedPast, useSimulatorItem, useSimulatorItems, useUpdateSimulatorItem } from "@/lib/simulator-queries";
import { isShortcutTarget, keyNavFor } from "@/lib/simulator-key-nav";
import { AnswerKeyForm, boxLayerLabel, evidenceOptionsFor } from "@/components/simulator/question-controls";
import { ModelSaysBox, SimulatorDeliveryView } from "@/components/simulator/delivery-clone";
import type { PhotoBoxLayer } from "@/components/simulator/photo-box-panel";
import type { HighlightLayer } from "@/components/simulator/text-highlight";
import { MAX_BOXES, boxesOf, describeKeyIssue, highlightsOf, normalizeKeyBoxes, photoIndexOf, type PhotoBox } from "@/lib/simulator-key-pointers";
import { isNA, readinessByPhase, SIMULATOR_PHASES, type AnswerKey, type QuestionId } from "@shared/simulator-helpers";
import { Button } from "@/components/ui/button";

const T = {
  border: "#E5E7EB",
  text: "#111827",
  txt2: "#6B7280",
  txt3: "#9CA3AF",
  purple: Starling.purple,
  good: "#166534",
  goodBg: "#DCFCE7",
  bad: "#B91C1C",
  badBg: "#FEF2F2",
  amber: "#B45309",
  amberBg: "#FEF3C7",
  sub: "#F3F4F6",
} as const;

const COMPLETION_NOTE_ELEMENT_ID = "simulator-completion-note";
const DELIVERY_DETAILS_ELEMENT_ID = "simulator-delivery-details";

export function ReadinessBadges({ readiness, eligiblePhases, testIdPrefix }: {
  readiness: ReturnType<typeof readinessByPhase>;
  eligiblePhases?: number[];
  testIdPrefix?: string;
}) {
  return (
    <div style={{ display: "inline-flex", gap: 4 }} data-testid={testIdPrefix}>
      {SIMULATOR_PHASES.map((p) => {
        const r = readiness[p];
        const eligible = !eligiblePhases || eligiblePhases.includes(p);
        const title = r.ready ? `Phase ${p}: ready` : `Phase ${p}: missing ${r.missing.join(", ")}`;
        return (
          <span
            key={p}
            title={title}
            data-testid={testIdPrefix ? `${testIdPrefix}-${p}` : undefined}
            data-ready={r.ready ? "true" : "false"}
            style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center", width: 22, height: 22, borderRadius: 6,
              fontSize: 11.5, fontWeight: 700, fontFamily: Starling.fontBody,
              background: r.ready ? T.goodBg : T.sub, color: r.ready ? T.good : T.txt3,
              border: `1px solid ${r.ready ? "#86EFAC" : T.border}`, opacity: eligible ? 1 : 0.45,
            }}
          >
            {p}
          </span>
        );
      })}
    </div>
  );
}

export default function TrainManageItem() {
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const { isAdmin, isSenior } = useRole();
  const detail = useSimulatorItem(id);
  const list = useSimulatorItems();
  const [, setLocation] = useLocation();
  const update = useUpdateSimulatorItem();
  const regrade = useRegradeSimulatorItem();
  const refreshPast = useRefreshVerifiedPast();

  const [key, setKey] = useState<AnswerKey>({});
  const [phases, setPhases] = useState<number[]>([]);
  const [dirty, setDirty] = useState(false);
  // Carousel position — one question card at a time beside the clone.
  const [activeIndex, setActiveIndex] = useState(0);
  // Prev / Next / next-draft walk the training set in list order (2026-09-14).
  const nav = useMemo(() => keyNavFor(list.data?.items ?? [], id), [list.data?.items, id]);
  // A new item starts on Q1, whichever card the last one was left on.
  useEffect(() => { setActiveIndex(0); }, [id]);

  // Unsaved key changes (boxes, highlights, feedback) must not vanish on a
  // reload or a click on ←. Browser navigations get the native prompt; the
  // in-app back link asks first.
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  /** Leave for another item; asks first when the key has unsaved changes. */
  const goTo = (targetId: string | null, opts: { skipGuard?: boolean } = {}) => {
    if (!targetId) return;
    if (dirty && !opts.skipGuard && !window.confirm("You have unsaved key changes. Leave without saving?")) return;
    setLocation(`/train/manage/item/${targetId}`);
  };
  // Alt+← / Alt+→ move between deliveries (Option on a Mac); ignored while typing in a field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!e.altKey || e.metaKey || e.ctrlKey) return;
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      if (!isShortcutTarget(e.target)) return;
      e.preventDefault();
      goTo(e.key === "ArrowLeft" ? nav.prevId : nav.nextId);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav.prevId, nav.nextId, dirty]);

  // Reset the local draft whenever the server copy changes (save / regrade).
  useEffect(() => {
    if (!detail.data) return;
    setKey(normalizeKeyBoxes(detail.data.item.answerKey ?? {}));
    setPhases(detail.data.item.eligiblePhases ?? []);
    setDirty(false);
  }, [detail.data?.item.answerKeyVersion, detail.data?.item.updatedAt, detail.data?.item.id]);

  const questions = detail.data?.questions ?? [];
  const readiness = useMemo(() => readinessByPhase(key, questions), [key, questions]);
  const evidence = useMemo(() => (detail.data ? evidenceOptionsFor(detail.data.traineeView) : []), [detail.data]);
  const sortedQuestions = useMemo(() => [...questions].sort((a, b) => a.sort - b.sort), [questions]);

  // Pointers: every question owns a layer (photo boxes + text highlights);
  // the active question's layer is editable, the others show dimmed.
  const activeQuestion = sortedQuestions[activeIndex];
  const activeKind: string | null = activeQuestion?.id ?? null;
  const activeLabel = activeQuestion ? boxLayerLabel(activeQuestion.id, activeQuestion.sort) : undefined;
  const pointerLayers: Array<PhotoBoxLayer & HighlightLayer> = useMemo(
    () => sortedQuestions.map((q) => ({ kind: q.id, label: boxLayerLabel(q.id, q.sort), boxes: boxesOf(key, q.id), highlights: highlightsOf(key, q.id) })),
    [key, sortedQuestions],
  );
  const onChangeActiveBoxes = (boxes: PhotoBox[]) => {
    if (!activeQuestion) return;
    const qid = activeQuestion.id as QuestionId;
    if (boxes.length > MAX_BOXES) { toast({ title: `At most ${MAX_BOXES} boxes per question`, variant: "destructive" }); return; }
    const entry: any = { ...(key[qid] ?? {}) };
    const added = boxes.length > boxesOf(key, qid).length ? boxes[boxes.length - 1] : null;
    entry.boxes = boxes;
    const photoNo = added ? photoIndexOf(added.target) : null;
    if (photoNo && (qid === "q1_street" || qid === "q2_unit" || qid === "q4_actual_dz" || qid === "q8_missed_return")) {
      // A box on photo N is evidence on photo N: keep `where` in step, and
      // for q1/q2 a boxed value is by definition visible in the POD.
      const src = `photo:${photoNo}`;
      const where: string[] = Array.isArray(entry.where) ? entry.where : [];
      if (!where.includes(src)) entry.where = [...where, src].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
      // …unless the unit is keyed N/A, where the POD sub-questions are not asked.
      if ((qid === "q1_street" || qid === "q2_unit") && !entry.inPod && !(qid === "q2_unit" && isNA(entry.value))) entry.inPod = "yes";
    }
    setKey({ ...key, [qid]: entry });
    setDirty(true);
  };

  if (!isAdmin && !isSenior) return <div style={{ padding: 24 }}>Senior or Admin access required.</div>;
  if (detail.isLoading) return <div style={{ padding: 24 }}>Loading item…</div>;
  if (detail.isError || !detail.data) return <div style={{ padding: 24 }}>Item not found. <Link href="/train/manage">Back to Training</Link></div>;

  const { item, traineeView, gradedAnswers } = detail.data;

  const save = async (): Promise<boolean> => {
    try {
      const res = await update.mutateAsync({ id: item.id, answerKey: normalizeKeyBoxes(key), eligiblePhases: phases });
      toast({ title: `Key saved · v${res.item.answerKeyVersion}`, description: res.item.status === "ready" ? "Ready for every eligible phase." : "Still draft — see the readiness badges." });
      return true;
    } catch (e: any) {
      // apiRequest throws `${status}: ${body}`; the body carries zod issues for a 400.
      const msg = String(e?.message ?? e);
      let description = msg;
      try {
        const body = JSON.parse(msg.replace(/^\d{3}:\s*/, ""));
        const first = describeKeyIssue(body?.issues?.[0], (qid) => sortedQuestions.find((q) => q.id === qid)?.sort);
        if (first) description = `${first}${body.issues.length > 1 ? ` (+${body.issues.length - 1} more)` : ""}`;
      } catch { /* not JSON — keep the raw message */ }
      toast({ title: "Couldn't save key", description, variant: "destructive" });
      return false;
    }
  };

  /** Save, then move to the next delivery; stays put (with the error toast) when the save fails. */
  const saveAndNext = async () => {
    if (dirty && !(await save())) return;
    goTo(nav.nextId, { skipGuard: true });
  };

  const doRefreshPast = async () => {
    try {
      const r = await refreshPast.mutateAsync(item.id);
      toast({ title: "Verified past refreshed", description: `${r.verifiedPast} past photo${r.verifiedPast === 1 ? "" : "s"} at this address · ${r.verifiedPastCount}× verified.` });
    } catch (e: any) {
      toast({ title: "Refresh failed", description: String(e?.message ?? e), variant: "destructive" });
    }
  };

  const doRegrade = async () => {
    try {
      const r = await regrade.mutateAsync(item.id);
      toast({ title: `Regraded ${r.regraded} answer${r.regraded === 1 ? "" : "s"}`, description: `Against key v${r.keyVersion} · ${r.attempts} attempt${r.attempts === 1 ? "" : "s"} re-summarised.` });
    } catch (e: any) {
      toast({ title: "Regrade failed", description: String(e?.message ?? e), variant: "destructive" });
    }
  };

  const statusStyle = item.status === "ready" ? { background: T.goodBg, color: T.good } : item.status === "retired" ? { background: T.sub, color: T.txt2 } : { background: T.amberBg, color: T.amber };

  return (
    <div style={{ padding: "14px 22px 40px", fontFamily: Starling.fontBody, color: T.text }} data-testid="train-manage-item">
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, marginBottom: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <Link
            href="/train/manage"
            data-testid="train-manage-item-back"
            onClick={(e: React.MouseEvent) => { if (dirty && !window.confirm("You have unsaved key changes. Leave without saving?")) e.preventDefault(); }}
            style={{ width: 26, height: 26, border: `1px solid ${T.border}`, borderRadius: 6, background: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", color: T.txt2, textDecoration: "none" }}
          >
            ←
          </Link>
          <div style={{ fontFamily: Starling.fontHead, fontSize: 18, fontWeight: 600 }}>Answer key · {item.recipientName ?? item.onfleetShortId ?? item.id.slice(0, 8)}</div>
          <span data-testid="item-status" style={{ padding: "3px 9px", borderRadius: 999, fontSize: 11.5, fontWeight: 700, ...statusStyle }}>{item.status}</span>
          <span style={{ fontSize: 12, color: T.txt2 }}>key v{item.answerKeyVersion}</span>
          <ReadinessBadges readiness={readiness} eligiblePhases={phases} testIdPrefix="editor-readiness" />
          {item.photoMissing && <span style={{ fontSize: 12, color: T.bad, fontWeight: 600 }}>⚠ photo missing on CloudFront</span>}
          {/* Shown once the list has loaded — before that "0 in set" and two dead arrows would flash. */}
          {list.data && (
          <div data-testid="key-nav" style={{ display: "inline-flex", alignItems: "center", gap: 6, marginLeft: 6 }}>
            <button
              type="button"
              data-testid="key-nav-prev"
              disabled={!nav.prevId}
              title={nav.prevId ? "Previous delivery (Alt+←)" : "First delivery in the set"}
              onClick={() => goTo(nav.prevId)}
              style={{ width: 26, height: 26, border: `1px solid ${T.border}`, borderRadius: 6, background: "#fff", color: nav.prevId ? T.text : T.txt3, cursor: nav.prevId ? "pointer" : "default", fontSize: 14 }}
            >
              ‹
            </button>
            <span data-testid="key-nav-position" style={{ fontSize: 12, color: T.txt2, minWidth: 52, textAlign: "center" }}>
              {nav.position ? `${nav.position} of ${nav.total}` : `${nav.total} in set`}
            </span>
            <button
              type="button"
              data-testid="key-nav-next"
              disabled={!nav.nextId}
              title={nav.nextId ? "Next delivery (Alt+→)" : "Last delivery in the set"}
              onClick={() => goTo(nav.nextId)}
              style={{ width: 26, height: 26, border: `1px solid ${T.border}`, borderRadius: 6, background: "#fff", color: nav.nextId ? T.text : T.txt3, cursor: nav.nextId ? "pointer" : "default", fontSize: 14 }}
            >
              ›
            </button>
            {nav.nextDraftId && (
              <button
                type="button"
                data-testid="key-nav-next-draft"
                title="Jump to the next delivery whose key is still draft"
                onClick={() => goTo(nav.nextDraftId)}
                style={{ border: "none", background: "transparent", color: T.purple, fontSize: 12, fontWeight: 600, cursor: "pointer", padding: "0 4px" }}
              >
                Next draft →
              </button>
            )}
          </div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5 }}>
            <span style={{ color: T.txt2 }}>Eligible:</span>
            {SIMULATOR_PHASES.map((p) => {
              const on = phases.includes(p);
              return (
                <button
                  key={p}
                  type="button"
                  aria-pressed={on}
                  data-testid={`editor-phase-toggle-${p}`}
                  onClick={() => { setPhases(on ? phases.filter((x) => x !== p) : [...phases, p].sort()); setDirty(true); }}
                  style={{ width: 26, height: 26, borderRadius: 6, border: `1px solid ${on ? T.purple : T.border}`, background: on ? T.purple : "#fff", color: on ? "#fff" : T.txt2, fontWeight: 700, fontSize: 12, cursor: "pointer" }}
                >
                  {p}
                </button>
              );
            })}
          </div>
          {gradedAnswers.total > 0 && (
            <span data-testid="graded-answers-note" style={{ fontSize: 12.5, color: gradedAnswers.stale > 0 ? T.amber : T.txt2 }}>
              {gradedAnswers.stale > 0
                ? `${gradedAnswers.stale} of ${gradedAnswers.total} answers graded against an older key`
                : `${gradedAnswers.total} answer${gradedAnswers.total === 1 ? "" : "s"} graded against this key`}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            data-testid="simulator-refresh-verified-past"
            disabled={refreshPast.isPending}
            title="Re-read the Verified past photos for this address from the live delivery. Does not touch the POD photos or the key."
            onClick={doRefreshPast}
          >
            {refreshPast.isPending ? "Refreshing…" : "Refresh verified past"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            data-testid="simulator-regrade"
            disabled={gradedAnswers.total === 0 || regrade.isPending || dirty}
            title={dirty ? "Save the key first" : gradedAnswers.total === 0 ? "No answers to regrade yet" : "Recompute every answer on this item against the current key"}
            onClick={doRegrade}
          >
            {regrade.isPending ? "Regrading…" : "Regrade"}
          </Button>
          <Link
            href={`/train/preview/${item.id}`}
            data-testid="simulator-preview-link"
            title={dirty ? "Save the key first — the preview grades against the saved key" : "Open this item as a trainee would see it (nothing is recorded)"}
            onClick={(e: React.MouseEvent) => { if (dirty && !window.confirm("You have unsaved key changes. The preview grades against the SAVED key. Leave without saving?")) e.preventDefault(); }}
            style={{ fontSize: 12.5, color: T.purple, textDecoration: "none", fontWeight: 600, whiteSpace: "nowrap" }}
          >
            Preview as trainee ↗
          </Link>
          {dirty && !update.isPending && (
            <span data-testid="simulator-key-unsaved" style={{ padding: "3px 9px", borderRadius: 999, fontSize: 11.5, fontWeight: 700, background: T.amberBg, color: T.amber }}>
              Unsaved changes
            </span>
          )}
          <Button size="sm" data-testid="simulator-key-save" disabled={!dirty || update.isPending} onClick={save}>
            {update.isPending ? "Saving…" : "Save key"}
          </Button>
          <Button
            size="sm"
            variant={dirty ? "default" : "outline"}
            data-testid="simulator-key-save-next"
            disabled={update.isPending || (!dirty && !nav.nextId)}
            title={nav.nextId ? (dirty ? "Save this key and open the next delivery" : "Open the next delivery") : "Last delivery in the set — saves only"}
            onClick={saveAndNext}
          >
            {dirty ? "Save & next ›" : "Next ›"}
          </Button>
        </div>
      </div>

      {/* Two panes: clone left, one question card right (sticky, so it stays beside the photos while the clone scrolls). */}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.35fr) minmax(400px, 1fr)", gap: 14 }} data-testid="train-manage-item-grid">
        <div>
          <SimulatorDeliveryView
            snapshot={traineeView}
            completionNoteElementId={COMPLETION_NOTE_ELEMENT_ID}
            deliveryDetailsElementId={DELIVERY_DETAILS_ELEMENT_ID}
            statusLabel="Key editor · preview"
            pointers={{ layers: pointerLayers, activeKind, activeLabel, onChangeBoxes: onChangeActiveBoxes }}
          />
          <ModelSaysBox expected={item.snapshot.expected} actual={item.snapshot.actual} />
        </div>
        <div>
          <div style={{ position: "sticky", top: 12, maxHeight: "calc(100vh - 24px)", overflowY: "auto", paddingBottom: 4 }} data-testid="train-manage-item-key-pane">
            <div style={{ fontSize: 12.5, color: T.txt2, marginBottom: 8 }}>
              Step through the questions with ‹ ›. Highlight text in the Delivery Details card for Q1/Q2 and in the Completion Details card for Q7, then press “Use highlighted text”; drag on a photo to box it, or select card text and press “Highlight selected text for the trainee”, to show where to look for the current question.
            </div>
            <AnswerKeyForm
              questions={questions}
              answerKey={key}
              onChange={(next) => { setKey(next); setDirty(true); }}
              evidence={evidence}
              completionNoteElementId={COMPLETION_NOTE_ELEMENT_ID}
              deliveryDetailsElementId={DELIVERY_DETAILS_ELEMENT_ID}
              phases={phases}
              activeIndex={activeIndex}
              onActiveIndexChange={setActiveIndex}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
